/**
 * The pillar card, for public (logged-out) grammar pages.
 *
 * Visually this is GrammarPillarSelector's card: the same `.grammar-pillar-card`
 * class, the same #E1E4E8 border and 0_4px_20px shadow, the same oversized ghost
 * number in the corner, the same 70x70 black icon tile, the same decorative dots
 * and shine sweep, the same greyed sample-exercise block. All of that CSS already
 * exists in index.css and is simply reused.
 *
 * What is NOT here, because none of it can run for a logged-out visitor:
 * Firestore progress reads, the progress bar, subscriptionService's locked state,
 * and the click handler into GrammarExerciseEngine. "Start practice" becomes a
 * sign-up link instead.
 *
 * The sample exercise is deliberately inert — pointer-events-none, select-none,
 * no answer revealed. It shows what practice looks like without giving it away,
 * which is the same preview-then-gate boundary used for Redemittel.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, PlayCircle } from 'lucide-react';
import { exerciseTypeLabel } from './publicGrammarData';

/** Inert preview of one exercise. Mirrors ExercisePreview in GrammarPillarSelector. */
function SamplePreview({ exercise }) {
  if (!exercise) return null;
  const label = exerciseTypeLabel(exercise);
  const stimulus = exercise.stimulus || exercise.question;
  const options = Array.isArray(exercise.options) ? exercise.options.slice(0, 4) : [];

  return (
    <div className="exercise-preview-section mt-4 pointer-events-none select-none">
      <div className="flex items-center justify-center gap-1.5 mb-1.5">
        <span className="text-[10px] font-bold tracking-widest uppercase text-black/40">Sample</span>
        <span className="text-[10px] font-bold tracking-widest uppercase text-black/40">·</span>
        <span className="text-[10px] font-bold tracking-widest uppercase text-black/40">{label}</span>
      </div>
      <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-left">
        {stimulus && (
          <p className="text-xs text-gray-700 leading-snug mb-2 line-clamp-2">{stimulus}</p>
        )}
        {options.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {options.map((o, i) => (
              <span
                key={i}
                className="px-2 py-1 bg-white border border-gray-300 rounded-lg text-xs text-gray-700"
              >
                {o}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PublicPillarCard({ topic, index, lang, exerciseCount, sample }) {
  const href = `/${lang}/grammar/${topic.level}/${topic.slug}`;

  return (
    <div className="grammar-pillar-card rounded-lg sm:rounded-3xl border border-[#E1E4E8] bg-white text-black shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
      <div className="p-6 sm:p-8 relative">
        {/* Ghost number, as in the app */}
        <div className="grammar-number-bg absolute top-4 right-6 font-space text-[4rem] font-black text-gray-200 opacity-50 leading-none">
          {String(index + 1).padStart(2, '0')}
        </div>

        <div className="grammar-decorative-dot grammar-dot-1" />
        <div className="grammar-decorative-dot grammar-dot-2" />
        <div className="grammar-decorative-dot grammar-dot-3" />
        <div className="grammar-shine-effect" />

        <div
          className="grammar-icon-animated relative w-[70px] h-[70px] rounded-2xl flex items-center justify-center text-white mb-6 mx-auto"
          style={{ backgroundColor: '#000000' }}
        >
          <BookOpen className="w-10 h-10" aria-hidden="true" />
        </div>

        <h3 className="grammar-title-animated font-space text-xl sm:text-2xl font-black text-black text-center mb-2 leading-tight">
          <Link to={href} className="hover:opacity-80 transition-opacity duration-200">
            {topic.title}
          </Link>
        </h3>

        {topic.subtitle && (
          <p className="text-sm text-center text-gray-600 mb-4 line-clamp-2">{topic.subtitle}</p>
        )}

        <SamplePreview exercise={sample} />

        {exerciseCount > 0 && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-4 mt-4">
            <PlayCircle className="w-4 h-4" aria-hidden="true" />
            <span>{exerciseCount} exercises</span>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <Link
            to={href}
            className="flex-1 py-2 px-3 bg-white text-black border-2 border-black rounded-lg text-sm font-semibold hover:bg-black hover:text-white transition-all flex items-center justify-center gap-1.5 min-h-[44px]"
          >
            <BookOpen className="w-4 h-4" aria-hidden="true" />
            Read the rules
          </Link>
          <Link
            to={`/${lang}/auth?mode=signup`}
            className="flex-1 py-2 px-3 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-1.5 min-h-[44px] transition-opacity hover:opacity-90"
            style={{ backgroundColor: '#006D77' }}
          >
            <PlayCircle className="w-4 h-4" aria-hidden="true" />
            Start practice
          </Link>
        </div>
      </div>
    </div>
  );
}
